import React from 'react';
import OrgGitHubConnection from './OrgGitHubConnection';
import OrgGitLabConnection from './OrgGitLabConnection';
import OrgAzureDevOpsConnection from './OrgAzureDevOpsConnection';
import OrgCodeCommitConnection from './OrgCodeCommitConnection';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useEnabledProviders } from '../../hooks/useEnabledProviders';
import { GitProvider, ProviderIcon, providerLabel } from '../../config/providers';

interface OrgIntegrationsSectionProps {
  organizationId: string;
}

interface ProviderConnectionProps {
  organizationId: string;
  readOnly?: boolean;
}

/** Connection panel per provider. Providers without an entry have no settings UI and are skipped. */
const PROVIDER_CONNECTIONS: Partial<Record<GitProvider, React.ComponentType<ProviderConnectionProps>>> = {
  github: OrgGitHubConnection,
  gitlab: OrgGitLabConnection,
  azure_devops: OrgAzureDevOpsConnection,
  codecommit: OrgCodeCommitConnection,
};

const OrgIntegrationsSection: React.FC<OrgIntegrationsSectionProps> = ({ organizationId }) => {
  const { currentOrganization } = useOrganization();
  const enabledProviders = useEnabledProviders();
  const isReadOnly =
    !!currentOrganization?.is_demo ||
    currentOrganization?.current_user_role === 'viewer';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-ui-text-primary">
          Integrations
        </h3>
        <p className="text-sm text-ui-text-secondary mt-1">
          Connect external services to enable organization-wide features.
        </p>
      </div>

      {enabledProviders.map((provider) => {
        const Connection = PROVIDER_CONNECTIONS[provider];
        if (!Connection) return null;
        return (
          <div
            key={provider}
            className="bg-ui-background-tertiary rounded-lg border border-ui-border-primary p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <ProviderIcon provider={provider} className="w-5 h-5 text-ui-text-primary" />
              <h4 className="text-lg font-semibold text-ui-text-primary">
                {providerLabel(provider)}
              </h4>
            </div>
            <Connection organizationId={organizationId} readOnly={isReadOnly} />
          </div>
        );
      })}
    </div>
  );
};

export default OrgIntegrationsSection;
